import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  Avatar,
  AvatarSize,
  Button,
  ButtonSize,
  HoverStyles,
  PaddingModes,
  Text,
  ThumbnailImage,
} from 'wix-ui-tpa/cssVars';
import { DiscountName } from '../../DiscountName/DiscountName';
import { ReadMoreLink } from '../../ReadMoreLink/ReadMoreLink';
import { CourseBadge } from '../../CourseBadge/CourseBadge';
import { CourseDetails } from '../../CourseDetails/CourseDetails';
import {
  AgendaItemLayoutProps,
  STAFF_LOCATION_SEPARATOR_DATA_HOOK,
} from '../const';
import { useShowsStaffAndLocation } from '../useShowsStaffAndLocation';
import { classes, st } from './AgendaItemSideBySide.st.css';

export const AgendaItemSideBySideLayout: React.FC<AgendaItemLayoutProps> = ({
  agendaItemViewModel,
  onSlotClick,
  onReadMoreClick,
}) => {
  const {
    serviceName,
    serviceImage,
    startTime,
    duration,
    staffName,
    staffImage,
    location,
    spots,
    price,
    priceBeforeDiscount,
    priceAfterDiscount,
    discountName,
    showCalculatedAtCheckout,
    buttonText,
    disabled,
    slotId,
    readMoreText,
    readMoreAriaLabel,
    course,
  } = agendaItemViewModel || {};
  const { isMobile } = useEnvironment();
  const showsStaffAndLocation = useShowsStaffAndLocation({
    staffName,
    location,
  });

  // TODO: This is List layout example, should make adjustment for side by side layout
  return (
    <div
      data-hook="agenda-item-side-by-side-layout"
      className={st(classes.root, {
        isMobile,
      })}
    >
      <ThumbnailImage {...serviceImage} className={classes.serviceImage} />

      <div className={st(classes.content)}>
        <div className={st(classes.topWrapper)}>
          <div className={st(classes.nameAndDurationWrapper)}>
            <div className={classes.serviceNameRow}>
              <Text className={classes.serviceName}>{serviceName}</Text>
              <CourseBadge badgeText={course?.badgeText} />
            </div>
            <Text className={classes.startTime}>
              {startTime} <Text className={classes.duration}>({duration})</Text>
            </Text>
            <ReadMoreLink
              slotId={slotId}
              readMoreText={readMoreText}
              readMoreAriaLabel={readMoreAriaLabel}
              onReadMoreClick={onReadMoreClick}
            />
          </div>
          <div
            className={st(classes.staffAndLocationWrapper, {
              isCourse: !!course,
            })}
          >
            {/*
              Its own row: this wrapper turns into `flex-direction: row` on
              mobile, so the course lines would sit inline with the avatar and
              the location instead of stacked above them.
            */}
            <div className={classes.courseDetailsRow}>
              <CourseDetails course={course} />
            </div>
            <div className={classes.staff}>
              <Avatar
                className={classes.staffImage}
                size={AvatarSize.small}
                src={staffImage}
                name={staffName}
              />
              <Text className={classes.staffName}>{`${staffName}`}</Text>
              {/*
                A course runs the two names along one line on every screen
                (Figma 4235-115684); a class only does it on a phone, where
                the wrapper is already a row. Both names have to be there, or
                the dot ends up with nothing on one side of it.
              */}
              {(isMobile || !!course) && showsStaffAndLocation && (
                <Text
                  className={classes.splitDot}
                  data-hook={STAFF_LOCATION_SEPARATOR_DATA_HOOK}
                  aria-hidden="true"
                >
                  •
                </Text>
              )}
            </div>
            <Text className={classes.location}>{location}</Text>
          </div>
        </div>
        <div className={st(classes.bottomWrapper)}>
          <div className={st(classes.priceAndSpotsWrapper)}>
            <div className={classes.priceContainer}>
              {priceBeforeDiscount && priceAfterDiscount ? (
                <div className={classes.pricesRow}>
                  <Text
                    className={classes.priceBeforeDiscount}
                    data-hook="agenda-item-price-before-discount"
                  >
                    {priceBeforeDiscount}
                  </Text>
                  <Text className={classes.price}>{priceAfterDiscount}</Text>
                </div>
              ) : (
                <Text className={classes.price}>{price}</Text>
              )}
              <DiscountName
                discountName={discountName}
                showCalculatedAtCheckout={showCalculatedAtCheckout}
              />
            </div>
            <Text className={classes.spots}>{spots}</Text>
          </div>
          {buttonText && (
            <div className={st(classes.buttonWrapper)}>
              <Button
                data-hook="agenda-item-cta-button"
                onClick={() => onSlotClick(slotId)}
                disabled={disabled}
                className={classes.button}
                size={ButtonSize.small}
                paddingMode={PaddingModes.explicitPaddings}
                hoverStyle={HoverStyles.underline}
              >
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
